/**
 * SizerScreen — RTL/GUC loan pricing "sizer".
 *
 * Left: a react-final-form scenario (Loan Details + Applicants + Properties).
 * Right (sticky): the priced-result rail + the market-data card.
 *
 * The form posts LOAN-MODEL VOCABULARY — the same field names a deal uses —
 * because that is what makes "Save as deal" a straight re-post of the priced
 * payload to `/underwriting/create_loan` with no mapping layer. Anything the
 * backend has to rename for us comes back in `payloadWarnings` and is a bug to
 * fix, not a notice to dismiss.
 *
 * Notable deviations from the digital_sizer source, per the eastviewos port brief:
 *   • No @tanstack/react-query — price/save are plain async handlers tracking
 *     pending/error/data in useState.
 *   • HTTP via the shared dsHttp client (sizerApi); paths omit the `/api` prefix.
 *   • The whole exceptions subsystem (inline callout, request modal,
 *     loanExceptionsApi) is dropped — only the pure pricing result renders.
 *   • Save success → notistack snackbar with the deal id, then navigate to the
 *     existing /loan/:id route.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Form } from "react-final-form";

import RffFormSpy from "../../components/finalForm/shared/RffFormSpy";
import arrayMutators from "final-form-arrays";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import SaveIcon from "@mui/icons-material/Save";

import { sizerApi, readScenarioId } from "../../api/sizerApi";
import LoanDetailsSection from "./sections/LoanDetailsSection";
import EntitySection from "./sections/EntitySection";
import BorrowersSection, { newBorrower } from "./sections/BorrowersSection";
import PropertiesSection, { newProperty } from "./sections/PropertiesSection";
import RtlResultsColumn from "./components/RtlResultsColumn";
import MarketDataPanel from "./components/MarketDataPanel";
import { fmtMoney, fmtPct } from "./sizerFormat";
import { isoDateFromToday } from "../sizersShared/sizerFormat";
import { normalizeLegacyDelayedPurchase } from "../../utils/loans/delayedPurchase";
import { Callout } from "../sizersShared/sizerResultUi";
import SizerWorkspace from "../sizersShared/workspace/SizerWorkspace";
import {
  SIZE_BY,
  SIZE_BY_FIELD,
  sizeByFor,
} from "../sizersShared/LeverageOrAmountField";
import ExplainResultPanel from "../sizersShared/workspace/ExplainResultPanel";
import {
  propertyFromStored,
  sizingInputFor,
} from "../sizersShared/FinancedCostsFields";
import {
  WORKSPACE_MODES,
  pricedToastText,
  useWorkspaceMode,
} from "../sizersShared/workspace/workspaceModel";
import { scenarioInputSummary } from "../sizersShared/scenarioModel";
import { estimateAmounts } from "../sizersShared/sizerAmounts";
import { RtlKpis, RtlPanels } from "./components/RtlReview";
import RequestExceptionModal from "../GucSizerScreen/components/RequestExceptionModal";
import { pendingExceptionCodes } from "../GucSizerScreen/gucConstants";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import {
  stripFormOnlyKeys,
  FormCard,
  PrimaryButton,
  SecondaryButton,
  ActionNote,
  SizerDevBar,
  SizerPayloadPanel,
  useSizerDevTools,
  scrollSizerToTop,
  SZ,
} from "../sizersShared/sizerUi";
import { addressLabel } from "../../_src_shared/utils/functions/addressLabel";

/**
 * The form opens EMPTY. Only the two repeaters are seeded — with one blank row
 * each — so the user has an Applicant 1 / Property 1 card to type into rather
 * than an empty section and an "Add" button. Nothing here is a value: no
 * product, no purpose, no amounts.
 */
const EMPTY_DEAL = {
  borrowers: [newBorrower()],
  subjectProperties: [newProperty()],
};

/**
 * Sample scenario for the dev-only prefill button. Every selection is a value
 * the field actually offers (see sizersShared/sizerSelectOptions) — a prefilled
 * value is a posted value, so it obeys the same rule as the option lists, and
 * every FIELD NAME here is the canonical one (no `bestFico`, no
 * `requestedInitialLeverage`) so the demo can never itself trip a payload
 * warning.
 */
const DEMO_DEAL = {
  loanProductType: "Fix and Flip",
  loanPurpose: "Purchase",
  termMonths: 12,
  sizerInitialRequestedLeverage: 0.9,
  financedBudgetPercent: 1.0,
  // The DECLARED count is deal-level (BorrowersSection reads it there), and the
  // demo declares the one guarantor it then enters.
  numberOfGuarantors: 1,
  numberOfEntityMembers: 1,
  entity: { entityName: "Demo Holdings LLC", entityType: "LLC" },
  borrowers: [
    {
      ...newBorrower(),
      firstName: "Demo",
      lastName: "Borrower",
      ficoMid: 760,
      citizenshipStatus: "US Citizen",
      entityPercentOwnership: 1.0,
      cashLiquidity: 200000,
      otherLiquidity: 100000,
      experience: { fix_and_flip_sold: 12 },
    },
  ],
  subjectProperties: [
    {
      ...newProperty(),
      // Address is left to the Places picker (RffAddressEditingFieldGoogle),
      // which writes the whole Address object. Only the zip/state are seeded,
      // because the market-data panel keys off the zip.
      address: { zip: "90001", state: "CA" },
      propertyType: "SFR",
      asIsValue: 400000,
      purchasePrice: 400000,
      // ~30 days out: a fixed date ages into the past, and a past purchase date
      // on a non-delayed purchase describes a deal that can't be.
      purchaseDate: isoDateFromToday(30),
      constructionBudget: 100000,
      arv: 620000,
      residentialSqFootage: 1800,
      closingCosts: 12000,
      financedClosingCosts: false,
      financedReserve: false,
    },
  ],
};

/**
 * SCENARIO-STAGE STUBS — sanctioned when pricing, stripped when saving.
 *
 * The 2026-08-17 phased input contract makes the sizer the deliberately loose
 * stage: a declared guarantor count and self-attested background answers are
 * ACCEPTED by `/scenario/rtl`, which turns each into a `dataChecks[]` entry to
 * reconcile against real data in processing (entered borrower rows, the
 * background report) rather than rejecting it.
 *
 * They are scenario-only, and the backend's normalizer does NOT remove them —
 * so posting the same payload to `/underwriting/create_loan` (which is the whole
 * point of speaking loan-model vocabulary) would persist them on the deal as
 * junk, since the LOS models are `extra=allow`. On the LOAN, guarantor count is
 * derived from `borrowers[].isGuarantor` and background flags are an extraction
 * artifact — neither has a settable field.
 */
const SCENARIO_ONLY_BORROWER_KEYS = [
  "numberOfGuarantors",
  "backgroundFlags",
  "backgroundFlagsPresent",
];

/**
 * The same rule at scenario level. `numberOfGuarantors` is now asked once for
 * the deal (it is what the backend reads first), and `numberOfEntityMembers` is
 * a count of owners still to be gathered — on a real loan those are
 * `entities[].entityMembers[]` records, not a number.
 */
const SCENARIO_ONLY_KEYS = ["numberOfGuarantors", "numberOfEntityMembers"];

/**
 * Form values → the scenario/loan payload.
 *
 * Only shape corrections live here — nothing is computed, defaulted or invented.
 * The form holds the borrowing entity under a single `entity` key because that
 * reads naturally as a form section; the wire shape is the loan's flat
 * `entities[]` array, with `borrowerEntity: true` marking the primary borrowing
 * entity (by convention also `entities[0]`, which one entity satisfies).
 *
 * `forSave` drops the scenario-stage stubs above. The address is passed through
 * WHOLE: the Google-place extras (`id`, `countryCode`, `coordinateTuple`,
 * `score`) used to be stripped here as vendor junk, but the backend now
 * documents them as keep — `extra=allow` persists them and the place `id` is
 * wanted for property de-duplication. All that is required is that the canonical
 * Address fields are populated, which the Places picker does.
 */
function buildPayload(values, { forSave = false } = {}) {
  const { entity, ...rest } = values;

  const payload = {
    ...rest,
    borrowers: (values.borrowers || []).map((b) => {
      if (!forSave || !b) return b;
      const out = { ...b };
      SCENARIO_ONLY_BORROWER_KEYS.forEach((k) => delete out[k]);
      return out;
    }),
  };

  if (forSave) SCENARIO_ONLY_KEYS.forEach((k) => delete payload[k]);
  // Which field the user drove (§12b): in leverage mode a financed reserve
  // grosses the initial loan amount up; an entered amount is taken as is. A
  // scenario-only key, so the save payload does not carry it.
  if (!forSave) {
    const sizingInput = sizingInputFor(values, values[SIZE_BY_FIELD]);
    if (sizingInput) payload.sizingInput = sizingInput;
  }
  // Form-only answers (`_delayedPurchase`, `_sizeBy`) never post.
  stripFormOnlyKeys(payload);

  if (entity?.entityName || entity?.entityType) {
    payload.entities = [{ ...entity, borrowerEntity: true }];
  }

  return payload;
}

/**
 * The inverse of `buildPayload` — a stored scenario back into form values, for
 * resuming a persisted scenario (`?scenario=<id>`, from Priced Scenarios).
 *
 * Only the two shape corrections are undone (`entities[]` → the form's single
 * `entity`, and the repeaters get a blank row rather than being empty): the
 * stored payload IS loan-model vocabulary, which is exactly why it can be loaded
 * straight back into the form that produced it.
 */
function payloadToValues(stored) {
  if (!stored) return null;
  // A legacy "Delayed Purchase" purpose reads as cash-out + the flag (REQ-106).
  const scenario = normalizeLegacyDelayedPurchase(stored);
  /* The STORED scenario is the backend-normalized one: it carries the
     baseLoanAmount / totalHoldback / totalLoanAmount the normalizer derived.
     The backend only derives those when they are blank, so seeding them back
     into the form would freeze the amounts — a later leverage or budget edit
     would move nothing. Totals are always derived; the advance is kept only
     when no leverage is set (then it was the user's own amount override). */
  // `sizingInput` is re-derived from the form's own leverage / amount choice.
  const { entities, totalHoldback, totalLoanAmount, baseLoanAmount, sizingInput, ...rest } =
    scenario;
  const leverageSet =
    rest.sizerInitialRequestedLeverage != null && rest.sizerInitialRequestedLeverage !== "";
  const entity =
    (entities || []).find((e) => e?.borrowerEntity) || (entities || [])[0] || null;

  return {
    ...rest,
    ...(!leverageSet && baseLoanAmount != null ? { baseLoanAmount } : null),
    // Sized by leverage unless only an advance was set (then by amount).
    [SIZE_BY_FIELD]: sizeByFor({
      sizerInitialRequestedLeverage: rest.sizerInitialRequestedLeverage,
      baseLoanAmount: leverageSet ? null : baseLoanAmount,
    }),
    ...(entity ? { entity } : null),
    borrowers: rest.borrowers?.length ? rest.borrowers : [newBorrower()],
    subjectProperties: rest.subjectProperties?.length
      ? rest.subjectProperties.map(propertyFromStored)
      : [newProperty()],
  };
}

/* The running estimate (base, holdback, total) is the shared one —
   sizersShared/sizerAmounts `estimateAmounts`, the same copy the GUC sizer
   uses — so the two sizers cannot show a loan differently. */

export default function SizerScreen() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [pricePending, setPricePending] = useState(false);
  const [priceError, setPriceError] = useState(null);
  const [result, setResult] = useState(null);
  const [savePending, setSavePending] = useState(false);
  // WHEN the scenario on screen was priced, stamped into the results header so
  // an edited-but-not-rerun form is visibly not what the numbers describe.
  const [pricedAt, setPricedAt] = useState(null);
  // …and the values it was priced from, for the results header's context line.
  const [pricedValues, setPricedValues] = useState(null);
  /* The persisted scenario this screen is pricing.
   *
   * Every priced run is stored server-side as a `pricing_scenarios` doc with an
   * append-only version history — the drift record from the first declared terms
   * to the created loan. Sending the id back on the NEXT price is what makes the
   * run a version of the same scenario rather than a brand-new object, so this
   * is held in a ref: it must survive a reprice without being a render input,
   * and it is deliberately NOT cleared when the form is edited (editing then
   * repricing IS the drift the history exists to capture). The demo prefill
   * clears it — that is a different scenario, not a revision of this one.
   */
  const scenarioIdRef = useRef(null);
  /* Set right before this screen moves its OWN URL after a price (blank →
     `?scenario=<id>`, or dropping `&version=`). The resume effect then skips
     its re-seed: the form already holds what just priced, and re-seeding from
     the stored (normalized) payload would overwrite it. */
  const skipResumeRef = useRef(false);

  /* RESUME — `?scenario=<id>` from Priced Scenarios.
   *
   * Loads the stored inputs back into the form AND adopts the id, so the next
   * price continues that scenario's history instead of starting a new object.
   * Without this the id only lives as long as the page, and a scenario can never
   * be picked up again after navigating away — which is the whole reason the
   * pipeline screen exists.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  // `readScenarioId` and not `.get()`: a URL can carry `?scenario=null` (an
  // unresolved id interpolated by whatever built the link), and that string is
  // truthy — it would fetch `/pricing/scenarios/null` and 500. Not an id, no
  // resume: the page is simply a blank sizer.
  const resumeId = readScenarioId(searchParams.get("scenario"));

  /* Which version is loaded lives in the URL as well as in state, so a reload
     lands on the same inputs and a pasted link carries them. Cleared after a
     price: what is on screen is the NEW version by then, and a stale
     `&version=1` would re-seed the old one on the next reload. */
  const setVersionParam = (version) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (version) next.set("version", String(version));
        else next.delete("version");
        return next;
      },
      { replace: true },
    );
  // A SPECIFIC version's inputs, when the pipeline deep-linked one ("Open this
  // version"). Still a resume of the same scenario — pricing from here appends
  // the next version like any other edit, never a rollback.
  const resumeVersion = Number(searchParams.get("version")) || null;
  // Review | Edit inputs — `?mode=`, Review by default on a resumed scenario.
  const [mode, setMode] = useWorkspaceMode(Boolean(resumeId));
  const [seed, setSeed] = useState(null);
  // The resumed scenario’s metadata — what the banner reports.
  const [resumedDoc, setResumedDoc] = useState(null);
  // Which version's inputs the form was seeded from — the banner names it, and
  // an older one puts the banner in its "viewing an earlier version" state.
  const [loadedVersion, setLoadedVersion] = useState(null);

  useEffect(() => {
    if (!resumeId) return;
    if (skipResumeRef.current) {
      skipResumeRef.current = false;
      return;
    }
    let live = true;
    sizerApi
      .getScenario(resumeId)
      .then((doc) => {
        if (!live) return;
        scenarioIdRef.current = doc?.scenarioId || resumeId;
        setResumedDoc(doc || null);
        const pick = resumeVersion
          ? (doc?.versions || []).find((v) => v.version === resumeVersion)
          : null;
        setSeed(payloadToValues(pick?.scenario || doc?.scenario));
        setLoadedVersion(pick?.version || doc?.currentVersion || null);
        /* REPLAY THE STORED RESULT — no re-price to see a saved scenario.
           Every priced run persists its FULL response
           (PricingScenarioVersion.result) and GET /pricing/scenarios/{id}
           returns the doc unprojected, so the payload this rail renders is
           already in hand. Re-POSTing to see it would price against TODAY sheet
           and append a version — the act of looking would change the record. */
        const currentV = (doc?.versions || []).find(
          (v) => v.version === doc?.currentVersion,
        );
        const priced = pick ? pick.result : doc?.result;
        setResult(priced || null);
        setPricedValues(priced ? payloadToValues(pick?.scenario || doc?.scenario) : null);
        // The STORED timestamp, never now — on a resume this is history.
        setPricedAt(priced ? (pick || currentV)?.pricedAt || doc?.updatedAt || null : null);
      })
      .catch((e) =>
        enqueueSnackbar(
          e?.response?.data?.detail || "Could not load that scenario",
          { variant: "error" },
        ),
      );
    return () => {
      live = false;
    };
  }, [resumeId, resumeVersion, enqueueSnackbar]);

  const handlePrice = async (values) => {
    setPricePending(true);
    setPriceError(null);
    try {
      // The old singular `borrower` / `subjectProperty` mirror was dropped: the
      // backend treats those as duplicate off-contract keys, strips them and
      // reports a payloadWarning — the arrays are the contract. `buildPayload`
      // only reshapes (entity → entities[]); it computes nothing.
      const data = await sizerApi.price({
        ...buildPayload(values),
        ...(scenarioIdRef.current ? { scenarioId: scenarioIdRef.current } : null),
      });
      // Persistence is best-effort server-side: a run that failed to store comes
      // back without an id, and the next price simply starts a new scenario.
      if (data?.scenarioId) scenarioIdRef.current = data.scenarioId;
      const prevResult = result;
      const prevVersion = loadedVersion;
      setResult(data);
      setPricedValues(values);
      setPricedAt(new Date().toISOString());
      // What just priced IS the current version now — the header names the one
      // this run created rather than the one it was resumed from.
      if (data?.scenarioVersion) setLoadedVersion(data.scenarioVersion);
      enqueueSnackbar(
        pricedToastText({ scenarioType: "rtl", data, prevResult, prevVersion }),
        {
          variant: "success",
          action: data?.scenarioId
            ? (key) => (
                <Box
                  component="button"
                  type="button"
                  onClick={() => setMode(WORKSPACE_MODES.REVIEW)}
                  sx={{ border: 0, background: "none", color: "inherit", font: "inherit", fontWeight: 700, cursor: "pointer" }}
                >
                  View results
                </Box>
              )
            : undefined,
        },
      );
      if (data?.scenarioId) {
        // The workspace header reads the doc (name, versions, status) — re-read
        // it so the new version is in the picker.
        sizerApi
          .getScenario(data.scenarioId)
          .then((doc) => doc && setResumedDoc(doc))
          .catch(() => {});
        // A blank sizer becomes this scenario's workspace; a resumed one drops
        // a stale `&version=`. Either moves the URL, which must not re-seed.
        if (resumeId !== data.scenarioId || searchParams.get("version")) {
          skipResumeRef.current = true;
          setSearchParams(
            { scenario: data.scenarioId, mode: WORKSPACE_MODES.EDIT },
            { replace: true },
          );
        }
      }
    } catch (e) {
      setPriceError(
        new Error(e?.response?.data?.detail || e?.message || "Failed to price this loan."),
      );
    } finally {
      setPricePending(false);
    }
  };

  /* File an exception against the SCENARIO (pricing-scenario-sizers.md §6) —
     the same object and approvers a deal exception has, so it re-parents onto
     the loan on conversion. The response is the updated scenario doc, which the
     header and the "requested · pending" state both read. */
  const handleFileException = async (body) => {
    const doc = await sizerApi.createExceptionRequest(scenarioIdRef.current, body);
    if (doc) setResumedDoc(doc);
    enqueueSnackbar("Exception request filed", { variant: "success" });
    return doc;
  };

  const handleSave = async (values) => {
    setSavePending(true);
    try {
      // `forSave` drops the scenario-stage stubs — see buildPayload. Everything
      // else posts exactly as priced; that is why the scenario speaks loan-model
      // vocabulary in the first place.
      const data = await sizerApi.save(buildPayload(values, { forSave: true }));
      // `/underwriting/create_loan` returns the created loan document.
      const dealId = data?._id || data?.id;
      enqueueSnackbar(dealId ? `Saved as loan ${dealId}` : "Loan saved", {
        variant: "success",
      });
      // Link the persisted scenario to the deal it became. This marks it
      // Converted, and from then on the scenario is the baseline the loan's real
      // underwritten data is compared against. Best-effort: a failed link must
      // never look like a failed save — the loan exists either way.
      if (dealId && scenarioIdRef.current) {
        sizerApi
          .updateScenario(scenarioIdRef.current, {
            dealId,
            pipeline: "underwriting_pipeline",
          })
          .catch(() => {});
      }
      if (dealId) setTimeout(() => navigate(`/loan/${dealId}`), 700);
    } catch (e) {
      enqueueSnackbar(
        e?.response?.data?.detail || e?.message || "Failed to save loan.",
        { variant: "error" },
      );
    } finally {
      setSavePending(false);
    }
  };

  return (
    // <Form> wraps the shell (rather than the other way round) so the header
    // action can reach the form API to prefill it.
    <Form
      initialValues={EMPTY_DEAL}
      mutators={{ ...arrayMutators }}
      onSubmit={handlePrice}
    >
      {({ handleSubmit, values, form, invalid }) => (
        <SizerFormBody
          handleSubmit={handleSubmit}
          values={values}
          form={form}
          invalid={invalid}
          seed={seed}
          resumeId={resumeId}
          mode={mode}
          onModeChange={setMode}
          onDuplicate={() => {
            // Same inputs, new scenario: drop the id and the doc, keep the
            // form. The next price starts its own history.
            scenarioIdRef.current = null;
            setResumedDoc(null);
            setSeed(null);
            setLoadedVersion(null);
            setResult(null);
            setPricedAt(null);
            setPricedValues(null);
            navigate("/sizers/rtl");
            enqueueSnackbar("Duplicated — price it to save it as a new scenario", {
              variant: "success",
            });
          }}
          resumedDoc={resumedDoc}
          loadedVersion={loadedVersion}
          onResumedDocChange={setResumedDoc}
          // Loading an older version is the same seeding path a resume takes —
          // the stored payload IS form values, which is why the scenario speaks
          // loan-model vocabulary in the first place.
          onLoadVersion={(v) => {
            const vals = payloadToValues(v.scenario);
            setSeed(vals);
            setLoadedVersion(v.version);
            setVersionParam(v.version);
            // That run own stored result, so the rail shows what v1 actually
            // priced at rather than what today sheet says about v1 inputs.
            setResult(v.result || null);
            setPricedValues(v.result ? vals : null);
            setPricedAt(v.result ? v.pricedAt || null : null);
          }}
          result={result}
          pricedAt={pricedAt}
          pricedValues={pricedValues}
          onScenarioReset={() => {
            scenarioIdRef.current = null;
          }}
          pricePending={pricePending}
          priceError={priceError}
          savePending={savePending}
          onSave={handleSave}
          onFileException={handleFileException}
        />
      )}
    </Form>
  );
}

/**
 * SaveDealConfirmDialog — the gate in front of `/underwriting/create_loan`.
 *
 * Creating a deal is the one IRREVERSIBLE thing this screen does: a real loan
 * lands in the pipeline with an id, the scenario flips to Converted, and there
 * is no undo. It used to happen on a single click, which made a mis-click on a
 * scratch scenario somebody else's cleanup job.
 *
 * The dialog states what will be created, in the same terms the form asked for
 * it, and names what is NOT being captured yet — the fields deferred to the
 * create-deal stage. That list is the honest part: a loan created from here is
 * thinner than one created through the (unbuilt) create-deal step, and the
 * person clicking should know which fields they will be filling in later.
 */
const DEFERRED_AT_CREATE = [
  "Exit strategy",
  "Broker origination points",
  "Interest calculation",
  "Dutch interest",
  "Condition at origination",
  "Property status at origination",
];

const SummaryRow = ({ k, v }) => (
  <Flx
    jb
    g={1.25}
    sx={{
      alignItems: "baseline",
      py: "7px",
      borderBottom: `1px dashed ${SZ.line}`,
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Txt sx={{ fontSize: 12.5, color: SZ.ink500 }}>{k}</Txt>
    <Txt
      sx={{
        fontFamily: SZ.mono,
        fontVariantNumeric: "tabular-nums",
        fontSize: 12.5,
        fontWeight: 500,
        color: SZ.ink900,
        textAlign: "right",
      }}
    >
      {v || "—"}
    </Txt>
  </Flx>
);

function SaveDealConfirmDialog({ open, onClose, onConfirm, saving, values, est }) {
  const props = (values.subjectProperties || []).length;
  const applicants = (values.borrowers || []).length;
  const address = addressLabel(values.subjectProperties?.[0]?.address);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: "18px 22px 8px" }}>
        <Txt component="div" sx={{ fontSize: 16, fontWeight: 700, color: SZ.ink900 }}>
          Create a loan from this scenario?
        </Txt>
        <Txt component="div" sx={{ fontSize: 12.5, color: SZ.ink500, mt: "3px" }}>
          This posts to the pipeline immediately and cannot be undone.
        </Txt>
      </Box>

      <Box sx={{ p: "8px 22px 0" }}>
        <SummaryRow k="Product" v={values.loanProductType} />
        <SummaryRow k="Purpose" v={values.loanPurpose} />
        <SummaryRow k="Term" v={values.termMonths ? `${values.termMonths} months` : ""} />
        <SummaryRow
          k={
            values.sizerInitialRequestedLeverage != null
              ? "Requested leverage"
              : "Initial loan amount"
          }
          v={
            values.sizerInitialRequestedLeverage != null
              ? fmtPct(values.sizerInitialRequestedLeverage, 2)
              : values.baseLoanAmount
                ? fmtMoney(values.baseLoanAmount)
                : ""
          }
        />
        <SummaryRow
          k="Loan amount (estimate)"
          v={est.total > 0 ? fmtMoney(est.total) : ""}
        />
        <SummaryRow k="Entity" v={values.entity?.entityName} />
        <SummaryRow
          k="Applicants"
          v={`${applicants} applicant${applicants === 1 ? "" : "s"}`}
        />
        <SummaryRow
          k="Properties"
          v={
            props === 1 && address
              ? address
              : `${props} propert${props === 1 ? "y" : "ies"}`
          }
        />
      </Box>

      <Box sx={{ p: "16px 22px 0" }}>
        <Callout tone="warn" title="Collected later, not on this loan">
          {DEFERRED_AT_CREATE.join(" · ")} — plus applicant names and addresses
          where they were left blank.
        </Callout>
      </Box>

      <Flx jb ac g={1.5} sx={{ p: "18px 22px 20px", flexWrap: "wrap" }}>
        <ActionNote>The scenario stays in the pipeline either way.</ActionNote>
        <Flx ac g={1.25}>
          <SecondaryButton onClick={onClose} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={onConfirm}
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <SaveIcon />
              )
            }
          >
            {saving ? "Creating…" : "Create loan"}
          </PrimaryButton>
        </Flx>
      </Flx>
    </Dialog>
  );
}

function SizerFormBody({
  handleSubmit,
  values,
  form,
  invalid,
  seed,
  resumeId,
  mode,
  onModeChange,
  onFileException,
  onDuplicate,
  resumedDoc,
  loadedVersion,
  onLoadVersion,
  onResumedDocChange,
  result,
  pricedAt,
  pricedValues,
  onScenarioReset,
  pricePending,
  priceError,
  savePending,
  onSave,
}) {
  const est = useMemo(() => estimateAmounts(values), [values]);
  const { spyOpen, toggleSpy, payloadOpen, togglePayload } = useSizerDevTools();
  const shellRef = useRef(null);
  // Creating a loan is irreversible, so it goes through a confirm rather than
  // firing on the click that reaches it.
  const [confirmOpen, setConfirmOpen] = useState(false);
  // "Explain this result" — the floating panel, opened from Review.
  const [explainOpen, setExplainOpen] = useState(false);
  // The finding a Request exception button was pressed on (prefills the modal).
  const [exceptionPrefill, setExceptionPrefill] = useState(null);
  // Codes already filed and still open — those rows show "requested · pending".
  const pendingCodes = useMemo(() => pendingExceptionCodes(resumedDoc), [resumedDoc]);
  // A request needs a persisted scenario; a run persists one, so this is only
  // ever off for a never-priced blank sizer.
  const requestException = resumeId
    ? (finding) =>
        setExceptionPrefill({
          severity: finding.severity,
          code: finding.code,
          exceptionCategory: /leverage|ltv|ltc|arv/i.test(finding.code || "")
            ? "Leverage"
            : undefined,
          riskBucket: "Credit Structure",
          exceptionDescription: finding.message,
          citation: finding.citation,
          observedValue: finding.observedValue,
          threshold: finding.threshold,
          requiredDocuments: finding.requiredDocuments,
        })
    : null;
  /* "Reprice at this amount" writes the ACTUAL input — the advance — and clears
     leverage (the override wins server-side anyway), then takes the user to the
     form so they press Re-price with it in view. */
  const repriceAt = (amount) => {
    form.change("baseLoanAmount", Math.round(amount));
    form.change("sizerInitialRequestedLeverage", undefined);
    form.change(SIZE_BY_FIELD, SIZE_BY.AMOUNT);
    setExplainOpen(false);
    if (resumeId && mode !== WORKSPACE_MODES.EDIT) onModeChange?.(WORKSPACE_MODES.EDIT);
    scrollSizerToTop(shellRef.current);
  };

  const sp = (values?.subjectProperties && values.subjectProperties[0]) || null;
  const zip = sp?.address?.zip;

  // The Calculate button can be most of a screen below the top, and the answer
  // renders in a rail that starts up there. Put the top back in view so the
  // result is read from its first line — the rate — rather than from whatever
  // card happens to be level with the button. Keyed on `pricedAt`, stamped
  // fresh every run, so a re-price scrolls again.
  useEffect(() => {
    if (pricedAt) scrollSizerToTop(shellRef.current);
  }, [pricedAt]);

  // A resumed scenario arrives after mount (it takes a fetch), so the form is
  // reset onto it rather than passed as initialValues. `reset` and not a field
  // -by-field set: the stored payload is the whole scenario, and half-applying
  // it would leave the form describing neither run.
  /* Seed the form from a resumed scenario — ONCE per scenario.
   *
   * The guard is the whole point. react-final-form hands the render prop a NEW
   * `form` object on every render (it wraps `reset` to swallow event args), so
   * an effect that depends on `form` re-runs on EVERY render — and this one
   * calls `reset`. That is what made a resumed scenario impossible to edit: each
   * keystroke re-rendered, the effect fired, and the stored value was written
   * straight back over what had just been typed.
   *
   * Keyed on the seed object’s identity rather than a boolean, so loading a
   * DIFFERENT scenario still re-seeds. `reset` and not a field-by-field set: the
   * stored payload is the whole scenario, and half-applying it would leave the
   * form describing neither run. */
  const seededRef = useRef(null);
  useEffect(() => {
    if (!seed || seededRef.current === seed) return;
    seededRef.current = seed;
    form.reset(seed);
  }, [seed, form]);

  // The line above the results column describes the scenario that PRICED, not
  // whatever the form holds now — the form stays open and editable beside it.
  const pricedContext = useMemo(() => {
    const v = pricedValues || {};
    return [v.loanProductType, v.loanPurpose, v.termMonths ? `${v.termMonths} mo` : null]
      .filter(Boolean)
      .join(" · ");
  }, [pricedValues]);

  /* What the numbers on screen were priced from — the version the form was
     loaded from, or the last run this session. The workspace diffs the form
     against it for the "N changes since vN" card, the stale results and the
     Save as deal gate. */
  const baseline = pricedValues || seed || null;

  // The version on screen, for Review mode's amounts and inputs grid. The
  // stored scenario carries the normalizer's baseLoanAmount / totalHoldback /
  // totalLoanAmount, which the form values do not.
  const shownRun = (resumedDoc?.versions || []).find((v) => v.version === loadedVersion);
  const shownScenario =
    shownRun?.scenario ||
    (loadedVersion === resumedDoc?.currentVersion ? resumedDoc?.scenario : null);
  /* Until that stored scenario lands, the priced FORM values stand in — but
     they carry no derived amounts, and in amount mode the entered advance read
     as the total loan with the holdback missing. Fill the three amounts from
     the estimate so both sizing modes show advance + holdback the same way. */
  const pricedEstimate = useMemo(() => {
    if (!pricedValues) return null;
    const e = estimateAmounts(pricedValues);
    return {
      ...pricedValues,
      baseLoanAmount: e.base || null,
      totalHoldback: e.holdback || null,
      totalLoanAmount: e.total || null,
    };
  }, [pricedValues]);

  return (
    <SizerWorkspace
      rootRef={shellRef}
      adminTools={
        <SizerDevBar
          variant="menu"
          /* A different scenario, not a revision of the one on screen — drop
             the persisted id so the next price starts its own history. */
          onFillDemo={() => {
            onScenarioReset?.();
            form.reset(DEMO_DEAL);
          }}
          spyOpen={spyOpen}
          onToggleSpy={toggleSpy}
          payloadOpen={payloadOpen}
          onTogglePayload={togglePayload}
        />
      }
      onExplain={() => setExplainOpen(true)}
      title="RTL Sizer"
      subtitle="Size and price a fix & flip or bridge loan, then save it as a deal."
      scenarioId={resumeId}
      doc={resumedDoc}
      onDocChange={onResumedDocChange}
      typeLabel="RTL"
      shownVersion={loadedVersion}
      onLoadVersion={onLoadVersion}
      mode={mode}
      onModeChange={onModeChange}
      onDuplicate={resumeId ? onDuplicate : null}
      values={values}
      baseline={baseline}
      onDiscard={() => baseline && form.reset(baseline)}
      result={result}
      pricePending={pricePending}
      priceError={priceError}
      onPrice={() => handleSubmit()}
      blockedReason={
        invalid
          ? "Fill the required fields marked * — a missing one would silently misprice the scenario."
          : null
      }
      liveNote="Priced against the live RTL rate sheet."
      onSave={() => setConfirmOpen(true)}
      savePending={savePending}
      kpis={<RtlKpis result={result} scenario={shownScenario || pricedEstimate} version={loadedVersion} />}
      panels={
        <RtlPanels
          result={result}
          onExplain={() => setExplainOpen(true)}
          onRequestException={requestException}
          pendingCodes={pendingCodes}
          onRepriceAt={repriceAt}
        />
      }
      inputCells={scenarioInputSummary("rtl", shownScenario || pricedValues)}
      form={
        <Box component="form" onSubmit={handleSubmit}>
          {/* Plain cards, never collapsible ones. Pricing is an iterate loop —
              change a leverage, re-price, look again — so the inputs stay put
              and stay open beside the answer. One card per subject: terms,
              entity, people, collateral. */}
          <Flx column g={2.5}>
            <FormCard>
              <LoanDetailsSection first sizing={result?.sizing} />
            </FormCard>
            <FormCard>
              <EntitySection first />
            </FormCard>
            <FormCard>
              <BorrowersSection first />
            </FormCard>
            <FormCard>
              <PropertiesSection first result={result} />
            </FormCard>
          </Flx>

          {/* Dev tooling last, under the form it acts on. */}

          <RffFormSpy formSpy={spyOpen} />

          {/* The exact bodies this screen would POST — super-admin only. */}
          <SizerPayloadPanel
            enabled={payloadOpen}
            sections={[
              {
                label: "Price",
                endpoint: "POST /pricing/scenario/rtl",
                note: "Scenario-stage stubs included — the backend reconciles them into dataChecks.",
                value: buildPayload(values),
              },
              {
                label: "Save as deal",
                endpoint: "POST /underwriting/create_loan",
                note: "Same payload with the scenario-only stubs stripped.",
                value: buildPayload(values, { forSave: true }),
              },
            ]}
          />
        </Box>
      }
      rail={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* The form beside it is editable, so the answer says WHEN it was
              the answer. */}
          {pricedAt && (
            <Txt sx={{ fontSize: 11, color: SZ.ink400 }}>
              Priced {formatDateTime(pricedAt)}
              {pricedContext ? ` · ${pricedContext}` : ""}
            </Txt>
          )}
          <RtlResultsColumn
            result={result}
            pending={pricePending}
            error={null}
            onRequestException={requestException}
            pendingCodes={pendingCodes}
            onRepriceAt={repriceAt}
            onExplain={() => setExplainOpen(true)}
          />
        </Box>
      }
      railAfter={
        /* Pre-price only. Once a scenario prices, its own response carries the
           market block and the column renders it. */
        !result ? (
          <MarketDataPanel
            zipcode={zip}
            marketData={result?.marketData}
            marketAssessment={result?.marketAssessment}
          />
        ) : null
      }
    >
      <ExplainResultPanel
        open={explainOpen}
        onClose={() => setExplainOpen(false)}
        result={result}
        version={loadedVersion}
        pricedAt={pricedAt}
        scenarioId={resumeId}
        pendingCodes={pendingCodes}
        onRequestException={requestException}
        onRepriceAt={repriceAt}
      />
      <RequestExceptionModal
        open={Boolean(exceptionPrefill)}
        onClose={() => setExceptionPrefill(null)}
        onSubmit={onFileException}
        scenarioId={resumeId}
        prefill={exceptionPrefill}
      />
      <SaveDealConfirmDialog
        open={confirmOpen}
        saving={savePending}
        values={values}
        est={est}
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          await onSave(values);
          setConfirmOpen(false);
        }}
      />
    </SizerWorkspace>
  );
}
